/* import __COLOCATED_TEMPLATE__ from './manage-network.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask, timeout, restartableTask } from 'ember-concurrency';
import {
  COMPANY_NETWORK_PARAMS_FILTERS,
  COMPANY_NETWORK_PARAMS_SORT_TYPES,
  NETWORK_PARAMS_NETWORK_TYPES,
} from 'sh-www/constants/api/people';
import { USER } from 'sh-www/models/user';

const CLASS_NAME = 'SH-c-admin-company-manage-network';
const SEARCH_DEBOUNCE_MS = 500;

const createOptionLabelFromUser = (user) => {
  return user.fullName ? `${user.fullName}, ${user.email}` : user.email;
}

class AdminCompanyManageNetwork extends Component {
  @service('state-managers/application') applicationStateManager;
  @service('commands/company-connection-commands') companyConnectionCommands;
  @service session;
  @service shData;
  @service('commands/user-commands') userCommandService;

  // matching server response
  @tracked addToNetworkMatch = null;
  @tracked removeFromNetworkMatch = null;
  // selection made
  @tracked addToNetworkSelection = null;
  @tracked removeFromNetworkSelection = null;
  // search value
  @tracked addToNetworkValue = '';
  @tracked removeFromNetworkValue = '';

  get className() {
    return CLASS_NAME;
  }

  get networkCreators() {
    const user = this.removeFromNetworkMatch;
    if (!user) return;

    const label = createOptionLabelFromUser(user);
    return [{
      key: label,
      model: { id: user.id, label },
    }];
  }

  get outOfNetworkCreators() {
    const user = this.addToNetworkMatch;
    if (!user) return;

    const label = createOptionLabelFromUser(user);
    return [{
      key: label,
      model: { id: user.id, label },
    }];
  }

  get disableRemoveFromNetwork() {
    return this.removeFromNetwork.isRunning || !this.removeFromNetworkSelection;
  }

  get disableAddToNetwork() {
    return this.addToNetwork.isRunning || !this.addToNetworkSelection;
  }

  get hasExceededNetworkSizeLimit() {
    const { inviteMax, networkSize } = this.args.company;
    if (!inviteMax) return false;

    return networkSize >= inviteMax;
  }

  @action
  handleOptionClicked(key, option) {
    // store input value
    this[`${key}Value`] = option.label;

    // store selection
    this[`${key}Selection`] = option.id;
  }

  @action
  handleFieldBlur(key) {
    // convenience clearing of field
    if (
      !this[`${key}Selection`]
      && !this[`${key}Match`]
      && !this[`${key}Search`].isRunning
    ) {
      this[`${key}Value`] = '';
    }
  }

  @restartableTask
  *removeFromNetworkSearch(input) {
    // reset any selection
    this.removeFromNetworkSelection = null;
    this.removeFromNetworkValue = input;

    if (!input) {
      this.removeFromNetworkMatch = null;
      return;
    }

    yield timeout(SEARCH_DEBOUNCE_MS);

    const response = yield this.userCommandService.searchFreelancerUsers({
      perspectiveUserId: this.session.currentUser.id,
      perspectiveCompanyId: this.args.company.id,
      matchIdOrEmail: input,
      network: NETWORK_PARAMS_NETWORK_TYPES.COMPANY_INVITED,
      sort: COMPANY_NETWORK_PARAMS_SORT_TYPES.CREATED_AT,
      limit: 1,
    });

    this.shData.pushResponseToStore(response);
    const matches = this.shData.getModelArrayFromResponse(response, 'user');

    this.removeFromNetworkMatch = matches?.[0];
  }

  @restartableTask
  *addToNetworkSearch(input) {
    // reset any selection
    this.addToNetworkSelection = null;
    this.addToNetworkValue = input;

    if (!input) {
      this.addToNetworkMatch = null;
      return;
    }

    yield timeout(SEARCH_DEBOUNCE_MS);

    const response = yield this.userCommandService.searchFreelancerUsers({
      perspectiveUserId: this.session.currentUser.id,
      perspectiveCompanyId: this.args.company.id,
      matchIdOrEmail: input,
      network: NETWORK_PARAMS_NETWORK_TYPES.COMPANY_NOT_INVITED,
      sort: COMPANY_NETWORK_PARAMS_SORT_TYPES.CREATED_AT,
      limit: 1,
      filter: {
        [COMPANY_NETWORK_PARAMS_FILTERS.USER_TYPE]: USER.TYPES.SELLER,
      },
    });

    this.shData.pushResponseToStore(response);
    const matches = this.shData.getModelArrayFromResponse(response, 'user');

    this.addToNetworkMatch = matches?.[0];
  }

  @dropTask
  *removeFromNetwork() {
    const { removeFromNetworkSelection } = this;
    const { id } = this.args.company;

    yield this.companyConnectionCommands.updateNetworkConnection(id, removeFromNetworkSelection, false, false);
    this.applicationStateManager.addToast(`${this.removeFromNetworkMatch.fullName} removed from network`);
    yield this.args.reloadCompanyTask.perform();

    // reset
    this.removeFromNetworkSelection = null;
    this.removeFromNetworkMatch = null;
    this.removeFromNetworkValue = '';
  }

  @dropTask
  *addToNetwork() {
    const { addToNetworkSelection } = this;
    const { id } = this.args.company;

    yield this.companyConnectionCommands.updateNetworkConnection(id, addToNetworkSelection, true);
    this.applicationStateManager.addToast(`${this.addToNetworkMatch.fullName} added to network`);
    yield this.args.reloadCompanyTask.perform();

    // reset
    this.addToNetworkSelection = null;
    this.addToNetworkMatch = null;
    this.addToNetworkValue = '';
  }
}

export default AdminCompanyManageNetwork;
